import { z } from 'zod'
import { objectToCamel } from 'ts-case-convert'
import { priceCodeSchema, priceTierSchema } from './price'
import { collectionSchema } from './collection'
import { supplierSchema } from './user'
import {
  buttonSchema,
  feltSchema,
  ribknitSchema,
  suedeSchema,
  threadSchema,
  waistbandSchema,
  zippersSchema,
} from './material'

//* ---------------- BASE ---------------- *//
export const availabilitySchema = z
  .object({
    in_stock: z.boolean(),
    last_edit_date: z.coerce.date().nullable(),
    readiness: z.string(),
    restock_date: z.coerce.date().nullable(),
  })
  .transform(objectToCamel)

const alternateImageSchema = z.object({
  code: z.string(),
  description: z.string(),
  name: z.string(),
  url: z.string(),
})

const compositionSchema = z.object({
  id: z.number(),
  description: z.string(),
})

const fabricPatternSchema = z.object({
  id: z.number(),
  name: z.string(),
})

const fabricWeaveSchema = z.object({
  id: z.number(),
  name: z.string(),
})

const baseFabricHomeSchema = z.object({
  id: z.number(),
  active: z.boolean(),
  description: z.string().nullable(),
  trinity_fabric_number: z.string(),
  url: z.string(),
  picario_url: z.string().optional().nullable(),
  swatch_url: z.string(),
  has_image: z.boolean().nullable(),
})

const baseFabricSchema = baseFabricHomeSchema.extend({
  collection_id: z.number().nullable(),
  pattern_id: z.number().nullable(),
  weave_id: z.number().nullable(),
  price_tier: priceTierSchema.nullable(),
  discount: z.number().nullable(),
  favorite_id: z.number().nullable(),
  fabric_garment_type: z.number(),
  trim_garment_type: z.number().nullable(),
  material_type: z.string(),
  availability: availabilitySchema.nullable().optional(),
  supplier_fabric_number: z.string(),
})

const baseFabricExtendedSchema = baseFabricSchema.extend({
  country_origin: z.string(),
  fabric_grouping: z.string(),
  pattern: z.string(),
  usage: z.string(),
  fabric_year: z.number(),
  fabric_garment_types: z.array(z.string()),
  trim_garment_types: z.array(z.string()),
  weight: z.number().nullable(),
  lead_time: z.string(),
  alternate_images: z.array(alternateImageSchema).nullable(),
  related_trim: baseFabricSchema.nullable(),
  collection: z.lazy(() => collectionSchema.nullable()),
  fabric_pattern: fabricPatternSchema.nullable(),
  fabric_weave: fabricWeaveSchema.nullable(),
  price_code: priceCodeSchema,
  composition: compositionSchema,
})

const millSchema = z.object({
  id: z.number(),
  name: z.string(),
})

const premiumSchema = z.object({
  id: z.number(),
  description: z.string(),
  level: z.string(),
})

const labelSchema = z.object({
  id: z.number(),
  sku_id: z.number().nullable(),
  name: z.string(),
  use_label: z.boolean(),
})

const baseCareLabelSchema = z.object({
  id: z.number(),
  description: z.string().nullable(),
  override_text: z.string().nullable(),
  override_image: z.string().nullable(),
  instructions: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      category: z.string().nullable(),
      image: z.string().nullable(),
      text: z.string().nullable(),
    }),
  ),
})

const baseFabricMatchesSchema = z.object({
  lining: baseFabricSchema.nullable(),
  button: buttonSchema.nullable(),
  felt: feltSchema.nullable(),
  outerwear_button: buttonSchema.nullable(),
  ribknit: ribknitSchema.nullable(),
  suede: suedeSchema.nullable(),
  thread: threadSchema.nullable(),
  waistband: waistbandSchema.nullable(),
  zipper: zippersSchema.nullable(),
  created_at: z.coerce.date().nullable(),
  updated_at: z.coerce.date().nullable(),
})

const baseFabricCompositionSchema = z.object({
  id: z.number(),
  description: z.string(),
})

const baseFabricMillsSchema = z.object({
  id: z.number(),
  name: z.string(),
  is_visible: z.boolean(),
})

const baseFabricLabelSchema = z.object({
  id: z.number(),
  name: z.string(),
  use_label: z.boolean(),
})

//* ---------------- SCHEMAS ---------------- *//
export const fabricSchema = baseFabricSchema.transform(objectToCamel)
export const fabricHomeSchema = baseFabricHomeSchema.transform(objectToCamel)

export const fabricAutocompleteSchema = baseFabricHomeSchema
  .omit({
    url: true,
    picario_url: true,
    swatch_url: true,
    active: true,
  })
  .transform(objectToCamel)

export const fabricExtendedSchema = baseFabricExtendedSchema.transform(objectToCamel)

export const fabricFullSchema = baseFabricExtendedSchema.transform(objectToCamel)

export const fabricAdminSchema = baseFabricExtendedSchema
  .extend({
    fabric_width: z.coerce.number().nullable(),
    created_at: z.coerce.date(),
    cuttable_width: z.coerce.number().nullable(),
    premium_id: z.number().nullable(),
    mill_id: z.number().nullable(),
    composition_id: z.number().nullable(),
    label_id: z.number().nullable(),
    care_label_id: z.number().nullable(),
    supplier_price_meter: z.coerce.number().nullable(),
    supplier_price_yard: z.coerce.number().nullable(),
    sku_id: z.number().nullable(),
    supplier: supplierSchema.nullable(),
    mill: millSchema.nullable(),
    premium_level: premiumSchema.nullable(),
    label: labelSchema.nullable(),
    care_label: baseCareLabelSchema.nullable(),
  })
  .transform(objectToCamel)

export const fabricHealthMetricsSchema = z.record(
  z.string(),
  z
    .object({
      inventory: z.number().nullable().optional(),
      rate_of_sale: z.number().nullable().optional(),
      months_on_hand: z.number().nullable().optional(),
    })
    .transform(objectToCamel),
)

export const fabricMatchesSchema = baseFabricMatchesSchema.transform(objectToCamel).nullable()
export const careLabelSchema = baseCareLabelSchema.transform(objectToCamel)
export const fabricCompositionSchema = baseFabricCompositionSchema.transform(objectToCamel)
export const fabricMillsSchema = baseFabricMillsSchema.transform(objectToCamel)
export const fabricLabelSchema = baseFabricLabelSchema.transform(objectToCamel)
export const fabricNotificationResponseSchema = z
  .object({
    id: z.number(),
    fabric_id: z.number(),
    dealer_id: z.number(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
    notified_at: z.coerce.date().nullable(),
    email: z.string(),
    notes: z.string().nullable(),
    fabric: baseFabricExtendedSchema,
  })
  .transform(objectToCamel)

//* ---------------- TYPES ---------------- *//
declare global {
  namespace TrinityAPI {
    type FabricType = z.infer<typeof fabricSchema>
    type FabricHomeType = z.infer<typeof fabricHomeSchema>
    type FabricAutocompleteType = z.infer<typeof fabricAutocompleteSchema>
    type FabricExtendedType = z.infer<typeof fabricExtendedSchema>
    type FabricAdminType = z.infer<typeof fabricAdminSchema>
    type AvailabilityType = FabricExtendedType['availability']
    type FabricFullType = z.infer<typeof fabricFullSchema>
    type FabricHealthMetricsType = z.infer<typeof fabricHealthMetricsSchema>
    type FabricMatchesType = z.infer<typeof fabricMatchesSchema>
    type CareLabelType = z.infer<typeof careLabelSchema>
    type FabricCompositionType = z.infer<typeof fabricCompositionSchema>
    type FabricMillsType = z.infer<typeof fabricMillsSchema>
    type FabricLabelType = z.infer<typeof fabricLabelSchema>
    type FabricNotificationType = z.infer<typeof fabricNotificationResponseSchema>
  }
}

//* ---------------- HELPERS ---------------- *//
